import React, { useState, useMemo } from 'react';
    import { motion, AnimatePresence } from 'framer-motion';
    import * as FiIcons from 'react-icons/fi';
    import SafeIcon from '../../../common/SafeIcon';
    import { useAuth0Data } from '../../../context/Auth0DataContext';
    import { format, parseISO } from 'date-fns';
    import CreateProjectModal from '../../../components/ProjectManagement/CreateProjectModal';
    import CreateTaskModal from '../../../components/ProjectManagement/CreateTaskModal';

    const { FiList, FiEdit, FiTrash2, FiUser, FiCalendar, FiTarget, FiSave, FiX, FiEye, FiPlus, FiCheckSquare, FiSquare, FiInfinity, FiEdit3 } = FiIcons;

    // Default columns for fallback
    const DEFAULT_COLUMNS = [
      { id: 'planning', title: 'Planning', color: 'bg-blue-100' },
      { id: 'in-progress', title: 'In Progress', color: 'bg-yellow-100' },
      { id: 'review', title: 'Review', color: 'bg-purple-100' },
      { id: 'completed', title: 'Completed', color: 'bg-green-100' }
    ];

    const ProjectList = () => {
      const { data, updateItem, deleteItem, getAllUsers, user } = useAuth0Data();
      const [selectedOwner, setSelectedOwner] = useState('all');
      const [hiddenStatuses, setHiddenStatuses] = useState([]);
      const [sortBy, setSortBy] = useState('dueDate');
      const [editingProject, setEditingProject] = useState(null);
      const [viewingProject, setViewingProject] = useState(null);
      const [showCreateModal, setShowCreateModal] = useState(false);
      const [showTaskModal, setShowTaskModal] = useState(null);
      const [formData, setFormData] = useState({
        title: '',
        description: '',
        notes: '',
        owner: '',
        dueDate: '',
        status: 'planning',
        priority: 'medium',
        ongoing: false
      });

      const allUsers = useMemo(() => getAllUsers(), [getAllUsers]);

      // Get columns from context, use defaults if empty
      const columns = useMemo(() => {
        return data.projectColumns && data.projectColumns.length > 0
          ? data.projectColumns
          : DEFAULT_COLUMNS;
      }, [data.projectColumns]);

      // Convert column title to status slug (e.g., "In Progress" -> "in-progress")
      const titleToSlug = (title) => {
        return title.toLowerCase().replace(/\s+/g, '-');
      };

      const handleTaskToggle = (task, project) => {
        const newStatus = task.status === 'completed' ? 'todo' : 'completed';
        const updatedTasks = project.tasks.map(t => (t.id === task.id ? { ...t, status: newStatus } : t));
        updateItem('projects', project.id, { ...project, tasks: updatedTasks });
      };

      // Toggle a status in the hidden list
      const toggleHiddenStatus = (status) => {
        setHiddenStatuses(prev =>
          prev.includes(status)
            ? prev.filter(s => s !== status)
            : [...prev, status]
        );
      };

      const filteredProjects = useMemo(() => {
        let filtered = data.projects;
        // Hide projects with statuses in the hidden list
        if (hiddenStatuses.length > 0) {
          filtered = filtered.filter(project => !hiddenStatuses.includes(project.status));
        }
        if (selectedOwner !== 'all') {
          filtered = filtered.filter(project => project.owner === selectedOwner);
        }
        return [...filtered].sort((a, b) => {
          switch (sortBy) {
            case 'title':
              return a.title.localeCompare(b.title);
            case 'dueDate':
              // Handle ongoing projects (null due dates)
              if (!a.dueDate && !b.dueDate) return 0;
              if (!a.dueDate) return 1; // Ongoing projects go to the end
              if (!b.dueDate) return -1;
              return new Date(a.dueDate) - new Date(b.dueDate);
            case 'progress':
              const aProgress = a.tasks?.length > 0 ? (a.tasks.filter(t => t.status === 'completed').length / a.tasks.length) * 100 : 0;
              const bProgress = b.tasks?.length > 0 ? (b.tasks.filter(t => t.status === 'completed').length / b.tasks.length) * 100 : 0;
              return bProgress - aProgress;
            default:
              return 0;
          }
        });
      }, [data.projects, hiddenStatuses, selectedOwner, sortBy]);

      // Memoize stats for the selected owner
      const projectsForStats = useMemo(() => {
        return selectedOwner !== 'all'
          ? data.projects.filter(p => p.owner === selectedOwner)
          : data.projects;
      }, [data.projects, selectedOwner]);

      const handleEdit = (project) => {
        setEditingProject(project.id);
        setFormData({
          title: project.title,
          description: project.description,
          notes: project.notes || '',
          owner: project.owner,
          dueDate: project.dueDate || '',
          status: project.status,
          priority: project.priority || 'medium',
          ongoing: !project.dueDate
        });
      };

      const handleSave = () => {
        const updatedProject = { ...formData, dueDate: formData.ongoing ? null : formData.dueDate };
        updateItem('projects', editingProject, updatedProject);
        setEditingProject(null);
        setFormData({ title: '', description: '', notes: '', owner: '', dueDate: '', status: 'planning', priority: 'medium', ongoing: false });
      };

      const handleCancel = () => {
        setEditingProject(null);
        setFormData({ title: '', description: '', notes: '', owner: '', dueDate: '', status: 'planning', priority: 'medium', ongoing: false });
      };

      const handleCreateSuccess = () => {
        console.log('Project created successfully!');
      };

      const handleTaskSuccess = () => {
        console.log('Task created successfully!');
      };

      const getStatusColor = (status) => {
        switch (status) {
          case 'planning': return 'text-blue-600 bg-blue-100';
          case 'in-progress': return 'text-yellow-600 bg-yellow-100';
          case 'completed': return 'text-green-600 bg-green-100';
          case 'on-hold': return 'text-red-600 bg-red-100';
          case 'review': return 'text-purple-600 bg-purple-100';
          default: return 'text-gray-600 bg-gray-100';
        }
      };

      const getPriorityColor = (priority) => {
        switch (priority) {
          case 'high': return 'text-red-600 bg-red-100';
          case 'medium': return 'text-yellow-600 bg-yellow-100';
          case 'low': return 'text-green-600 bg-green-100';
          default: return 'text-gray-600 bg-gray-100';
        }
      };

      const getPriorityIcon = (project) => {
        if (!project.dueDate) return 'text-blue-600'; // Ongoing projects
        const days = Math.ceil((new Date(project.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
        if (days < 7) return 'text-red-600';
        if (days < 14) return 'text-yellow-600';
        return 'text-green-600';
      };

      return (
        <div className="space-y-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-soft border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiList} className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="ml-3 text-xl font-bold text-gray-900">Project List</h3>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                Create Project
              </button>
            </div>

            {/* Filters and Sort */}
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex flex-wrap gap-2 items-center">
                {/* My Projects Toggle */}
                <button
                  onClick={() => setSelectedOwner(selectedOwner === user?.name ? 'all' : user?.name || 'all')}
                  className={`flex items-center px-3 py-2 rounded-lg border transition-colors ${
                    selectedOwner === user?.name
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" />
                  My Projects
                </button>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Owner:</label>
                  <select
                    value={selectedOwner}
                    onChange={(e) => setSelectedOwner(e.target.value)}
                    className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      selectedOwner !== 'all' ? 'border-primary-500 bg-primary-50' : 'border-gray-300'
                    }`}
                  >
                    <option value="all">All Owners</option>
                    {user?.name && <option value={user.name}>My Projects ({user.name})</option>}
                    <optgroup label="Team Members">
                      {allUsers.filter(u => u.name !== user?.name).map(u => (
                        <option key={u.id} value={u.name}>{u.name}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Sort by:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                  <option value="dueDate">Due Date</option>
                  <option value="title">Title</option>
                  <option value="progress">Progress</option>
                </select>
              </div>
              </div>

              {/* Status Visibility Toggles */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Show:</span>
                {columns.map(col => {
                  const status = titleToSlug(col.title);
                  const isHidden = hiddenStatuses.includes(status);
                  const count = data.projects.filter(p => p.status === status).length;
                  return (
                    <button
                      key={col.id}
                      onClick={() => toggleHiddenStatus(status)}
                      className={`flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isHidden
                          ? 'bg-gray-100 text-gray-400 line-through'
                          : `${col.color} text-gray-700`
                      }`}
                    >
                      {col.title} ({count})
                    </button>
                  );
                })}
                {hiddenStatuses.length > 0 && (
                  <button
                    onClick={() => setHiddenStatuses([])}
                    className="text-sm text-primary-600 hover:text-primary-700 ml-2"
                  >
                    Show all
                  </button>
                )}
              </div>
            </div>

            {/* Project Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-xl font-bold text-gray-900">{projectsForStats.length}</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Planning</p>
                <p className="text-xl font-bold text-blue-600">{projectsForStats.filter(p => p.status === 'planning').length}</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-xl font-bold text-yellow-600">{projectsForStats.filter(p => p.status === 'in-progress').length}</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-xl font-bold text-green-600">{projectsForStats.filter(p => p.status === 'completed').length}</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-xl font-bold text-red-600">{projectsForStats.filter(p => p.dueDate && new Date(p.dueDate) < new Date() && p.status !== 'completed').length}</p>
              </div>
            </div>

            {/* Projects Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence>
                {filteredProjects.map((project, index) => {
                  const completedTasks = project.tasks?.filter(t => t.status === 'completed').length || 0;
                  const totalTasks = project.tasks?.length || 0;
                  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
                  return (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ delay: index * 0.05 }}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      {editingProject === project.id ? (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                              type="text"
                              value={formData.title}
                              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              value={formData.description}
                              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              <SafeIcon icon={FiEdit3} className="w-3 h-3 inline mr-1" />
                              Notes
                            </label>
                            <textarea
                              value={formData.notes}
                              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Additional notes, links, or context"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                            <select
                              value={formData.owner}
                              onChange={(e) => setFormData(prev => ({ ...prev, owner: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                              <option value="">Select Owner</option>
                              {allUsers.map(user => (
                                <option key={user.id} value={user.name}>{user.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">{formData.ongoing ? 'Ongoing Project' : 'Due Date'}</label>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  id={`ongoing-${project.id}`}
                                  checked={formData.ongoing}
                                  onChange={(e) => setFormData(prev => ({ ...prev, ongoing: e.target.checked, dueDate: e.target.checked ? '' : prev.dueDate }))}
                                  className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <label htmlFor={`ongoing-${project.id}`} className="text-sm text-gray-600 cursor-pointer">Ongoing</label>
                              </div>
                            </div>
                            {!formData.ongoing ? (
                              <input
                                type="date"
                                value={formData.dueDate}
                                onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              />
                            ) : (
                              <div className="flex items-center justify-center h-10 px-3 py-2 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                                <SafeIcon icon={FiInfinity} className="w-5 h-5 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-500">No end date - ongoing project</span>
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                              <select
                                value={formData.status}
                                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              >
                                {columns.map(col => (
                                  <option key={col.id} value={titleToSlug(col.title)}>{col.title}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                              <select
                                value={formData.priority}
                                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={handleSave} className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                              <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" /> Save
                            </button>
                            <button onClick={handleCancel} className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors">
                              <SafeIcon icon={FiX} className="w-4 h-4 mr-2" /> Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="font-medium text-gray-900 text-lg line-clamp-2">{project.title}</h4>
                            <div className="flex items-center gap-1">
                              <button onClick={() => setShowTaskModal(project.id)} className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors" title="Add Task">
                                <SafeIcon icon={FiCheckSquare} className="w-4 h-4" />
                              </button>
                              <button onClick={() => setViewingProject(project)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                <SafeIcon icon={FiEye} className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleEdit(project)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors">
                                <SafeIcon icon={FiEdit} className="w-4 h-4" />
                              </button>
                              <button onClick={() => deleteItem('projects', project.id)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors">
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                          <div className="flex gap-2 mb-4">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>{project.status}</span>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(project.priority)}`}>{project.priority}</span>
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center text-gray-600">
                                <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" />
                                {project.owner}
                              </div>
                              <div className={`flex items-center ${getPriorityIcon(project)}`}>
                                <SafeIcon icon={project.dueDate ? FiCalendar : FiInfinity} className="w-4 h-4 mr-2" />
                                {project.dueDate ? format(parseISO(project.dueDate), 'MMM dd, yyyy') : 'Ongoing'}
                              </div>
                            </div>
                            {/* Quick Task Completion */}
                            {project.tasks && project.tasks.length > 0 && (
                              <div className="space-y-2">
                                <div className="text-sm font-medium text-gray-700">Quick Task Actions:</div>
                                <div className="max-h-24 overflow-y-auto space-y-1">
                                  {project.tasks.slice(0, 3).map(task => (
                                    <div key={task.id} className="flex items-center text-sm">
                                      <button onClick={() => handleTaskToggle(task, project)} className={`mr-2 ${task.status === 'completed' ? 'text-green-600' : 'text-gray-400'} hover:scale-110 transition-transform`}>
                                        <SafeIcon icon={task.status === 'completed' ? FiCheckSquare : FiSquare} className="w-4 h-4" />
                                      </button>
                                      <span className={`truncate ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-700'}`}>{task.title}</span>
                                    </div>
                                  ))}
                                  {project.tasks.length > 3 && (
                                    <div className="text-xs text-gray-500 ml-6">+{project.tasks.length - 3} more tasks</div>
                                  )}
                                </div>
                              </div>
                            )}
                            <div>
                              <div className="flex justify-between text-sm text-gray-600 mb-2">
                                <span>Progress</span>
                                <span>{completedTasks}/{totalTasks} tasks</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-primary-500 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                              </div>
                              <div className="text-right text-xs text-gray-500 mt-1">{progress.toFixed(0)}% complete</div>
                            </div>
                          </div>
                        </>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
            {filteredProjects.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <SafeIcon icon={FiTarget} className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No projects found for the selected filter</p>
              </div>
            )}
          </motion.div>

          {/* Project Details Modal */}
          {viewingProject && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setViewingProject(null)}>
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-xl shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Project Details</h3>
                  <button onClick={() => setViewingProject(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">{viewingProject.title}</h4>
                    <p className="text-gray-600">{viewingProject.description}</p>
                  </div>
                  {viewingProject.notes && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 flex items-center mb-1">
                        <SafeIcon icon={FiEdit3} className="w-4 h-4 mr-1" />
                        Notes
                      </label>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{viewingProject.notes}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Owner</label>
                      <p className="text-gray-900">{viewingProject.owner}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Due Date</label>
                      <p className="text-gray-900 flex items-center">
                        {viewingProject.dueDate ? (
                          <>
                            <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2" />
                            {format(parseISO(viewingProject.dueDate), 'MMMM dd, yyyy')}
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiInfinity} className="w-4 h-4 mr-2" />
                            Ongoing Project
                          </>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(viewingProject.status)}`}>{viewingProject.status}</span>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Priority</label>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(viewingProject.priority)}`}>{viewingProject.priority}</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-500">Tasks ({viewingProject.tasks?.length || 0})</label>
                      <button onClick={() => { setViewingProject(null); setShowTaskModal(viewingProject.id); }} className="flex items-center px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm">
                        <SafeIcon icon={FiPlus} className="w-3 h-3 mr-1" />
                        Add Task
                      </button>
                    </div>
                    {viewingProject.tasks?.length > 0 ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {viewingProject.tasks.map(task => (
                          <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <button onClick={() => handleTaskToggle(task, viewingProject)} className={`mr-3 ${task.status === 'completed' ? 'text-green-600' : 'text-gray-400'} hover:scale-110 transition-transform`}>
                                <SafeIcon icon={task.status === 'completed' ? FiCheckSquare : FiSquare} className="w-5 h-5" />
                              </button>
                              <div>
                                <p className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>{task.title}</p>
                                <p className="text-sm text-gray-600">{task.assignee}</p>
                              </div>
                            </div>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>{task.status}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No tasks assigned to this project</p>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Create Project Modal */}
          <CreateProjectModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={handleCreateSuccess} />

          {/* Create Task Modal */}
          <CreateTaskModal isOpen={showTaskModal !== null} onClose={() => setShowTaskModal(null)} projectId={showTaskModal} onSuccess={handleTaskSuccess} />
        </div>
      );
    };

    export default ProjectList;